<!-- 添加/编辑租户 -->
<template>
  <el-drawer
    v-model="layerFlag"
    :close-on-click-modal="true"
    direction="rtl"
    :destroy-on-close="true"
    :before-close="handleClose"
    size="50%">
    <template #title>
      <div class="drawer-title">{{ title }}</div>
    </template>
    <template #default>
      <div v-loading="loading">
        <custom-form
          v-if="['add', 'editTenant', 'batchEditTenant'].includes(types)"
          ref="refForm"
          :formData="formData"
          :formRules="formRules"
          @submitForm="submitForm"
        />
        <!-- 添加用户 -->
        <div v-if="types === 'add'" class="add-user">
          <el-button type="primary" @click="addUserFun">{{ t('user.addUser') }}</el-button>
        </div>
        <div v-for="(item, key) in userFormRefs" class="user-form-list">
          <div v-if="['add', 'editTenant'].includes(types)" class="remove-user">
            <span>{{ t('route.user') }}{{ key }}.</span>
            <el-icon class="remove-icon" @click="removeUser(key)">
              <Close />
            </el-icon>
          </div>
          <user-form
            :ref="el => setUserFormRef(el, key)"
            :key="key"
            :editType="types"
            :rowData="rowData"
            :scene="formData.scene.value || rowData.scene" />
        </div>
      </div>
    </template>
    <template #footer>
      <div>
        <!-- 取消 -->
        <el-button @click="handleClose">
          {{ $t('form.cancel') }}
        </el-button>
        <!-- 确认 -->
        <el-button
          type="primary"
          :loading="submitLoading"
          :disabled="loading"
          @click="submitFun">
          {{ $t('form.submit') }}
        </el-button>
      </div>
    </template>
  </el-drawer>
</template>

<script setup>
import { ref, computed, reactive, nextTick, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import checkRules from '@/components/CustomCheck'
import UserForm from '../user/com/addUser.vue'
import { addTenant, editTenant, batchEditTenant, addUser, verifyField } from '@/api/users/tenant'
import { updateUser, resetUserPassword } from '@/api/users/user'

const { t } = useI18n()
const layerFlag = ref(props.addUserLayer) // 是否显示弹框
const refForm = ref(null) // 表单ref
const userFormRefs = ref({}) // 用户表单ref
const emit = defineEmits(['update:addUserLayer', 'getList', 'update:editType', 'update:rowData'])

const loading = ref(false) // 表单提交loading
const submitLoading = ref(false) // 表单提交按钮loading
const types = ref()
const modifiedFields = ref(new Set()) // 跟踪被修改的字段

const props = defineProps({
  addUserLayer: {
    type: Boolean,
    default: false
  },
  editType: {
    type: String,
    default: ''
  },
  rowData: {
    type: Object,
    default: () => {
      return {}
    }
  }
})
// 弹框标题
const title = computed(() => {
  const titleMap = {
    add: t('user.addTenant'),
    editTenant: t('user.edit_tenant'),
    batchEditTenant: t('table.batchModify'),
    addUser: t('user.addUser'),
    editUser: t('user.editUser'),
    resetPassword: t('user.resetPassword')

  }
  return titleMap[types.value]
})
// 租户表单
const formData = reactive({
  scene: {
    type: 'select',
    label: t('license.scene'),
    value: 'null',
    prop: 'scene',
    placeholder: '',
    disabled: types.value !== 'add',

    options: [
      {
        id: 'null', // 后端全部场景需要传递null值，下拉框不可将null作为id，此处转成string
        name: t('table.all')
      },
      {
        id: 'dr',
        name: t('license.dr')
      },
      {
        id: 'migration',
        name: t('license.migration')
      }
    ]
  },
  name: {
    type: 'text',
    label: t('user.tenantName'),
    value: '',
    prop: 'name',
    placeholder: ''
  },
  status: {
    type: 'select',
    label: t('table.status'),
    value: null,
    prop: 'status',
    placeholder: '',
    options: [
      {
        id: 'activated',
        name: t('table.enable')
      },
      {
        id: 'forbidden',
        name: t('table.disable')
      }
    ]
  },
  trialed: {
    type: 'select',
    label: t('user.trialStatus'),
    value: 'false',
    prop: 'trialed',
    placeholder: '',
    options: [
      {
        id: 'false',
        name: t('user.untrialed')
      },
      {
        id: 'true',
        name: t('user.trialed')
      }
    ]
  }
})
// 表单校验规则
const { emptCheck } = checkRules(t)
// 表单校验函数
const checkFun = async(rule, modleVal, callback) => {
  if (!modleVal.value) {
    callback(new Error(t('form.emptyErrMsg')))
  } else {
    // 新增/编辑租户名称校验
    if (
      types.value === 'add' ||
      (types.value === 'editTenant' && props.rowData.name !== modleVal.value)
    ) {
      const scene = Object.values(props.rowData).length
        ? props.rowData.scene
        : formData.scene.value === 'null'
          ? undefined
          : formData.scene.value
      const res = await verifyField({
        enterprise_name: modleVal.value,
        scene: scene
      })
      if (res.data['enterprise_name'].status === 'ok') {
        callback()
      } else {
        callback(new Error(res.data['enterprise_name'].msg))
      }
    }
  }
}
const formRules = computed(() => {
  const rules = {
    scene: emptCheck,
    status: emptCheck,
    trialed: emptCheck,
    name: [{ required: true, trigger: ['blur'], validator: checkFun }]
  }
  if (types.value === 'batchEditTenant') {
    delete rules.status
    delete rules.trialed
  }
  return rules
})
// 新增用户
const addUserFun = async() => {
  await nextTick()
  const userLength = Object.keys(userFormRefs.value).length
  userFormRefs.value[`${userLength + 1}`] = {}
  // 编辑用户
  if (types.value === 'editUser') {
    await nextTick()
    for (const key in props.rowData) {
      if (userFormRefs.value[1].formData[key] && props.rowData[key]) {
        userFormRefs.value[1].formData[key].value = props.rowData[key]
      }
    }
  }
  // 重置密码
  if (types.value === 'resetPassword') {
    await nextTick()
    for (const key in userFormRefs.value[1].formData) {
      if (key === 'password') continue
      delete userFormRefs.value[1].formData[key]
    }
  }
}
const setUserFormRef = (el, key) => {
  if (el) userFormRefs.value[key] = el
}
// 移除用户
const removeUser = (key) => {
  delete userFormRefs.value[key]
}
// 如果存在添加用户，处理用户数据
const handleUserData = async() => {
  if (Object.keys(userFormRefs.value).length) {
    const refs = userFormRefs.value
    const promises = Object.keys(refs).map(key => refs[key].submit())
    const results = await Promise.all(promises)
    const pass = results.every(item => !!item)
    if (pass) {
      const userData = {}
      for (const key in refs) {
        const userForm = refs[key].formData
        userData[key] = {}
        for (const ele in userForm) {
          if (userForm[ele].value) {
            userData[key][ele] = userForm[ele].value
          }
        }
      }
      const users = []
      for (const key in userData) {
        users.push(userData[key])
      }
      let postData = {}
      if (types.value === 'editUser') {
        for (const key in users[0]) {
          if (props.rowData[key] === users[0][key]) delete users[0][key]
        }
        postData = users[0]
      } else if (types.value === 'resetPassword') {
        postData.password = users[0].password
      } else {
        postData = {
          users: users
        }
      }
      return postData
    }
  }
}
// 表单提交
const submitForm = async(val) => {
  val.validate(async valid => {
    if (valid) {
      const formVal = {}
      for (const key in formData) {
        if (formData[key].value) {
          if (key === 'trialed') {
            formVal.configs = {
              [key]: formData[key].value === 'true'
            }
          } else {
            formVal[key] = formData[key].value
          }
        }
      }
      // 批量修改只提交 status 和 trialed
      if (types.value === 'batchEditTenant') {
        const enterprise_ids = Array.isArray(props.rowData)
          ? props.rowData.map(item => item.id)
          : [props.rowData.id]
        const update_data = {}
        if (modifiedFields.value.has('status') && formVal.status) {
          update_data.status = formVal.status
        }
        if (modifiedFields.value.has('trialed') && formVal.configs?.trialed !== undefined) {
          update_data.configs = {
            trialed:
              formVal.configs?.trialed === 'true' ||
              formVal.configs?.trialed === true ||
              false
          }
        }
        addTenantFun({ enterprise_ids, update_data })
        return
      }
      // 如果存在添加用户
      if (Object.keys(userFormRefs.value).length) {
        const users = await handleUserData()
        if (users) {
          addTenantFun({ ...formVal, ...users })
        }
      } else {
        addTenantFun(formVal)
      }
    }
  })
}

// 添加/编辑租户
const addTenantFun = async(formVal) => {
  if (formVal.scene === 'null') delete formVal.scene
  submitLoading.value = true
  try {
    let res
    switch (types.value) {
      case 'addUser': {
        // 添加用户
        res = await addUser({
          enterprise_id: props.rowData.id,
          users: formVal.users
        })
        break
      }
      case 'editUser': {
        // 编辑用户
        res = await updateUser({
          user_id: props.rowData.id,
          update_data: { ...formVal }
        })
        break
      }
      case 'resetPassword': {
        // 重置用户密码
        res = await resetUserPassword({
          user_id: props.rowData.id,
          ...formVal
        })
        break
      }
      case 'add': {
        // 添加租户
        res = await addTenant(formVal)
        break
      }
      case 'batchEditTenant': {
        // 批量编辑租户
        res = await batchEditTenant(formVal)
        break
      }
      default: {
        // 编辑租户
        for (const key in props.rowData) {
          if (props.rowData[key] === formVal[key]) delete formVal[key]
        }
        res = await editTenant({
          enterprise_id: props.rowData.id,
          update_data: { ...formVal }
        })
        break
      }
    }

    if (res.code === '00008002') {
      // 参数错误
      // 清除error msg
      for (const i in formData) {
        formData[i].msg = ''
      }
      // 赋值error msg
      for (const j in res.error.fields) {
        if (formData[j]) {
          formData[j].msg = res.error.fields[j][0]
        }
      }
      submitLoading.value = false
      return
    }
    submitLoading.value = false
    handleClose()
    emit('getList')
  } catch (error) {
    submitLoading.value = false
  }
}
// 弹框关闭
const handleClose = async() => {
  layerFlag.value = false
  types.value = null
  userFormRefs.value = {}
  for (const key in formData) {
    formData[key].value =
      key === 'scene' ? 'null' : key === 'trialed' ? 'false' : ''
    // 重置 visibility
    if (['scene', 'name'].includes(key)) {
      formData[key].visibility = true
    }
  }
  await nextTick()
  if (refForm.value) refForm.value.reset()
  emit('update:addUserLayer', false)
  emit('update:rowData', {})
}
// 表单提交
const submitFun = async() => {
  if (['addUser', 'editUser', 'resetPassword'].includes(types.value)) {
    const res = await handleUserData()
    if (res) {
      addTenantFun(res)
    }
    return
  }
  await nextTick()
  refForm.value.submit()
}

watch(
  () => props.addUserLayer,
  (val) => {
    layerFlag.value = val
    if (!val || !props.editType) return
    types.value = props.editType

    if (['addUser', 'editUser', 'resetPassword'].includes(types.value)) {
      addUserFun()
    } else {
      if (!formData) return
      formData.scene.disabled = types.value !== 'add'
    }
    // 批量修改只显示 status 和 trialed
    if (types.value === 'batchEditTenant') {
      formData.scene.visibility = false
      formData.name.visibility = false
      formData.status.visibility = true
      formData.trialed.visibility = true
    } else {
      formData.scene.visibility = true
      formData.name.visibility = true
    }
    for (const key in formData) {
      if (['scene', 'trialed'].includes(key)) continue
      formData[key].value = ''
    }
    if (types.value === 'batchEditTenant') {
      formData.status.value = ''
      formData.trialed.value = ''
      modifiedFields.value.clear() // 清空修改字段记录
    }
    // 编辑参数绑定
    if (!props.rowData || !val) return
    for (const key in props.rowData) {
      if (formData[key] && props.rowData[key])
        formData[key].value = props.rowData[key]
      if (props.rowData.configs && props.rowData.configs.trialed)
        formData.trialed.value = props.rowData.configs.trialed.toString()
    }
  },
  { deep: true, immediate: true }
)

// 监听 status 和 trialed 的变化，记录被修改的字段
watch(
  () => formData.status.value,
  (newVal, oldVal) => {
    if (types.value === 'batchEditTenant' && newVal !== undefined) {
      modifiedFields.value.add('status')
    }
  }
)

watch(
  () => formData.trialed.value,
  (newVal, oldVal) => {
    if (types.value === 'batchEditTenant' && newVal !== undefined) {
      modifiedFields.value.add('trialed')
    }
  }
)
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
.add-user {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 16px;
}
.user-form-list {
  margin-top: 16px;
  .remove-user {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: $grey-8;
    margin-bottom: 16px;
    .remove-icon {
      font-size: 20px;
      cursor: pointer;
    }
    & > span {
      font-size: 14px !important;
    }
  }
}
</style>
